import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import Layout from "@/components/Layout";
import SmoothScroll from "@/components/SmoothScroll";
import Home from "@/pages/Home";

const About = lazy(() => import("@/pages/About"));
const ProductsIndex = lazy(() => import("@/pages/ProductsIndex"));
const Category = lazy(() => import("@/pages/Category"));
const Product = lazy(() => import("@/pages/Product"));
const Industries = lazy(() => import("@/pages/Industries"));
const GlobalShipping = lazy(() => import("@/pages/GlobalShipping"));
const QualityControl = lazy(() => import("@/pages/QualityControl"));
const Certifications = lazy(() => import("@/pages/Certifications"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const Contact = lazy(() => import("@/pages/Contact"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Admin = lazy(() => import("@/pages/Admin"));
const Portal = lazy(() => import("@/pages/Portal"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));

const Loader = () => (
  <div className="min-h-screen flex items-center justify-center bg-[hsl(var(--navy))]">
    <div className="font-mono-l text-xs text-[hsl(var(--brass-2))] tracking-[0.3em] animate-pulse">INGREN — LOADING</div>
  </div>
);

function AppRouter() {
  const location = useLocation();
  // Emergent Google Auth returns to /portal#session_id=... — intercept synchronously
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/admin" element={<Admin />} />
      <Route path="/admin/products" element={<Admin />} />
      <Route path="/portal" element={<Portal />} />
      <Route element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/products" element={<ProductsIndex />} />
        <Route path="/products/:categorySlug" element={<Category />} />
        <Route path="/products/:categorySlug/:productSlug" element={<Product />} />
        <Route path="/industries" element={<Industries />} />
        <Route path="/global-shipping" element={<GlobalShipping />} />
        <Route path="/quality-control" element={<QualityControl />} />
        <Route path="/certifications" element={<Certifications />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <SmoothScroll>
          <Toaster position="top-center" richColors />
          <Suspense fallback={<Loader />}>
            <AppRouter />
          </Suspense>
        </SmoothScroll>
      </BrowserRouter>
    </HelmetProvider>
  );
}
